import "@/App.css";
import { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Show } from "@clerk/react";
import { Toaster } from "@/components/ui/sonner";
import { PurposeGate } from "@/components/area/SupportPurpose";
import { TooltipProvider } from "@/components/ui/tooltip";
import ClerkAuthProvider from "@/components/auth/ClerkAuthProvider";
import AdminGate from "@/components/auth/AdminGate";
import AuthPage from "@/pages/AuthPage";
import TestLogin from "@/pages/TestLogin";
import Dashboard from "@/pages/Dashboard";
import NewProject from "@/pages/NewProject";
import Editor from "@/pages/Editor";
import Preview from "@/pages/Preview";
import Settings from "@/pages/Settings";
import Backup from "@/pages/Backup";
import Costs from "@/pages/Costs";
import Sites from "@/pages/Sites";
import SiteDetail from "@/pages/SiteDetail";
import Clients from "@/pages/Clients";
import SupportInbox from "@/pages/SupportInbox";
import AutomationDiagnostics from "@/pages/AutomationDiagnostics";
import WalletReconciliation from "@/pages/WalletReconciliation";
import EmailActivation from "@/pages/EmailActivation";
import PilotDiary from "@/pages/PilotDiary";
import CustomerHealth from "@/pages/CustomerHealth";
import PrivacyAdmin from "@/pages/PrivacyAdmin";
import PrivacyRequestPage from "@/pages/PrivacyRequestPage";
import Hosting from "@/pages/Hosting";
import PublicDemo from "@/pages/PublicDemo";
import Onboarding from "@/pages/Onboarding";
import ClientReview from "@/pages/ClientReview";
import InvitePage from "@/pages/InvitePage";
import MemberArea from "@/pages/MemberArea";
import Crm from "@/pages/Crm";

const LANGS = ["it", "es", "en"];
const MarketingSite = lazy(() => import("@/marketing/MarketingSite"));
const LangRedirect = lazy(() => import("@/marketing/MarketingSite").then((m) => ({ default: m.LangRedirect })));
// On the public host (future musora.io) "/" opens the site; elsewhere "/" stays the protected app.
const ON_MARKETING_HOST = !!process.env.REACT_APP_MARKETING_HOST && window.location.hostname === process.env.REACT_APP_MARKETING_HOST;

const APP_TITLE = "MUSORA";

function AdminArea() {
  return (
    <>
      <Show when="signed-in">
        <AdminGate>
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/new" element={<NewProject />} />
            <Route path="/projects/:id" element={<Editor />} />
            <Route path="/preview/:id" element={<Preview />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/backup" element={<Backup />} />
            <Route path="/costs" element={<Costs />} />
            <Route path="/sites" element={<Sites />} />
            <Route path="/sites/:id" element={<SiteDetail />} />
            <Route path="/clients" element={<Clients />} />
            <Route path="/support" element={<SupportInbox />} />
            <Route path="/automation-diagnostics" element={<AutomationDiagnostics />} />
            <Route path="/wallet-reconciliation" element={<WalletReconciliation />} />
            <Route path="/email-activation" element={<EmailActivation />} />
            <Route path="/pilot-diary" element={<PilotDiary />} />
            <Route path="/customer-health" element={<CustomerHealth />} />
            <Route path="/privacy-requests" element={<PrivacyAdmin />} />
            <Route path="/hosting" element={<Hosting />} />
            <Route path="/crm" element={<Crm />} />
            <Route path="/crm/:tid" element={<Crm />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AdminGate>
      </Show>
      <Show when="signed-out"><Navigate to={`/sign-in?redirect_url=${encodeURIComponent(window.location.href)}`} replace /></Show>
    </>
  );
}

function MemberRoute() {
  return (
    <>
      <Show when="signed-in"><MemberArea /></Show>
      <Show when="signed-out"><Navigate to={`/sign-in?redirect_url=${encodeURIComponent(window.location.href)}`} replace /></Show>
    </>
  );
}

function AdminRoutes() {
  const { pathname } = useLocation();
  useEffect(() => { document.title = APP_TITLE; }, [pathname]);
  return (
    <ClerkAuthProvider>
      <Routes>
        <Route path="/sign-in/*" element={<AuthPage mode="sign-in" />} />
        <Route path="/accesso-classico/*" element={<AuthPage mode="classic" />} />
        <Route path="/accesso-test" element={<TestLogin />} />
        <Route path="/sign-up/*" element={<AuthPage mode="sign-up" />} />
        <Route path="/invite/:token" element={<InvitePage />} />
        <Route path="/area" element={<MemberRoute />} />
        <Route path="/area/:tid" element={<MemberRoute />} />
        <Route path="/*" element={<AdminArea />} />
      </Routes>
    </ClerkAuthProvider>
  );
}

export default function App() {
  return (
    <div className="App">
      <TooltipProvider delayDuration={250}>
        <BrowserRouter>
          <Routes>
            {LANGS.map((l) => <Route key={l} path={`/${l}/*`} element={<Suspense fallback={null}><MarketingSite lang={l} /></Suspense>} />)}
            <Route path="/sito" element={<Suspense fallback={null}><LangRedirect /></Suspense>} />
            {ON_MARKETING_HOST && <Route path="/" element={<Suspense fallback={null}><LangRedirect /></Suspense>} />}
            <Route path="/demo/:slug" element={<PublicDemo />} />
            <Route path="/richiesta-privacy" element={<PrivacyRequestPage />} />
            <Route path="/richiesta-privacy/verifica/:token" element={<PrivacyRequestPage />} />
            <Route path="/richiesta-privacy/stato/:token" element={<PrivacyRequestPage status />} />
            <Route path="/onboarding/:token" element={<Onboarding />} />
            <Route path="/revisione/:token" element={<ClientReview />} />
            <Route path="/*" element={<AdminRoutes />} />
          </Routes>
        </BrowserRouter>
        <Toaster theme="light" position="bottom-right" richColors closeButton />
        <PurposeGate />
      </TooltipProvider>
    </div>
  );
}
